// Story 6.10 (ADO-1164) — My Account screen. Figma 532:2370.
// Story 6.31 (ADO-1253) — re-chromed to the finalised Figma: the full SiteHeader
// (OQ-6.31a) over a page body whose single content card holds the vertical
// SideNav | divider | active panel. Behaviour/contracts unchanged from 6.10 —
// this is a styling + structure re-skin (AC1–AC3, AC5). The header search is a
// global affordance that routes to the catalogue (/products); depot/cart/offers
// render as SiteHeader's built-in placeholders.
//
// Realises the customer-facing My Account UI deferred by Story 6.7 AC3, using
// the Story 6.9 design system. Four tabbed panels behind a vertical SideNav:
//   Profile · Password · Notifications · Privacy & Data.
//
// - Profile / Notifications read + write the Story 6.7 membership profile API
//   (`GET`/`PATCH /me/profile`) via `profile.client.ts`. Auth0/claim-owned fields
//   (email, role, account type) render read-only (AC2). `email` comes from the
//   session ID-token claim (`identity.email`, same source as `role`) — the
//   `/me/profile` access token carries no email claim in Phase 1, so the profile
//   API value is only a fallback. The AC2 "store name"
//   field is DEFERRED — no session/TenantContext source exposes it yet; it renders
//   only when `session.storeName` is supplied (OQ-6.31b — never fake data).
// - Password triggers the Story 6.6 Auth0-hosted change-password flow (AC3) —
//   no password field exists in the app.
// - Privacy & Data calls the Story 6.13 GDPR export/erasure endpoints (AC5).
//
// Server remains authoritative (NFR18); a11y via the DS components (NFR24–26).

import { useCallback, useEffect, useState } from 'react';
import {
  Banner,
  Button,
  FormField,
  Icon,
  Modal,
  SideNav,
  Toggle,
  sideNavPanelId,
  sideNavTabId,
  type SideNavItem,
} from '../../design-system';
import { isAdminRole, roleLabel } from '../../auth/rbac';
import { CatalogueLinkingHeader } from '../Products/CatalogueLinkingHeader';
import {
  fetchProfile,
  requestDataExport,
  requestErasure,
  updateProfile,
  type Profile,
} from '../../api/profile.client';
import { ChangePassword } from '../ChangePassword';
import styles from './MyAccount.module.css';

const TABS: SideNavItem[] = [
  { id: 'profile', label: 'Profile' },
  { id: 'password', label: 'Password' },
  { id: 'notifications', label: 'Notifications' },
  { id: 'privacy', label: 'Privacy & Data' },
];

// Stable, unique prefix for this screen's SideNav tab/panel ids (Story 6.9 API).
const NAV_ID = 'my-account';

export interface MyAccountProps {
  /** Trigger the Auth0-hosted change-password flow (Story 6.6); null when unavailable. */
  onRequestPasswordChange: (() => Promise<void>) | null;
  /** End the local and Auth0 sessions. */
  onSignOut?: (() => Promise<void>) | null;
  /** Read-only session values shown on the Profile tab (from claims / TenantContext). */
  session?: {
    storeName?: string | null;
    role?: string | null;
    accountType?: string | null;
    /** From the ID-token `email` claim (useSessionStore identity.email) — Auth0-owned. */
    email?: string | null;
  };
}

type SaveState = 'idle' | 'saving' | 'saved' | 'error';

export function MyAccount({ onRequestPasswordChange, onSignOut, session }: MyAccountProps) {
  const [active, setActive] = useState('profile');
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    let alive = true;
    fetchProfile()
      .then((p) => alive && setProfile(p))
      .catch(() => alive && setLoadError(true));
    return () => {
      alive = false;
    };
  }, []);

  return (
    <div className={styles.page}>
      {/* The full catalogue-linking header (Story 6.31, OQ-6.31a; ADO-1779): the
          same search typeahead, camera scan and in-place Browse menu as the
          catalogue, so the header reads the same on every page. */}
      <CatalogueLinkingHeader showManagerDashboard={isAdminRole(session?.role)} />

      <main className={styles.main}>
        <div className={styles.pageHead}>
          <h1 className={styles.pageTitle}>My Account</h1>
          <p className={styles.pageSubtitle}>
            Update your profile, password, notification and privacy preferences.
          </p>
          {onSignOut ? (
            <Button type="button" onClick={() => void onSignOut()}>
              Sign out
            </Button>
          ) : null}
        </div>

        <div className={styles.card}>
          <div className={styles.layout}>
            <div className={styles.nav}>
              <SideNav
                idPrefix={NAV_ID}
                label="Account sections"
                items={TABS}
                activeId={active}
                onChange={setActive}
              />
            </div>
            <div
              className={styles.content}
              id={sideNavPanelId(NAV_ID, active)}
              role="tabpanel"
              aria-labelledby={sideNavTabId(NAV_ID, active)}
            >
              {loadError ? (
                <Banner type="error" title="Couldn't load your account">
                  Please reload the page and try again.
                </Banner>
              ) : active === 'profile' ? (
                <ProfilePanel profile={profile} session={session} onSaved={setProfile} />
              ) : active === 'password' ? (
                <PasswordPanel onRequestPasswordChange={onRequestPasswordChange} />
              ) : active === 'notifications' ? (
                <NotificationsPanel profile={profile} onSaved={setProfile} />
              ) : (
                <PrivacyDataPanel />
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

/**
 * Seed value for the editable "Display name" when the profile has none stored.
 *
 * Deliberately reads the app-owned `firstName`/`lastName` PROFILE columns, never
 * the registry `fullName` — AC7 requires the registry identity stay a separate,
 * read-only field that is never used as an editable-name fallback and never
 * PATCHed back.
 */
function profileNameFallback(profile: Profile): string {
  return [profile.firstName, profile.lastName].filter(Boolean).join(' ');
}

function ProfilePanel({
  profile,
  session,
  onSaved,
}: {
  profile: Profile | null;
  session?: MyAccountProps['session'];
  onSaved: (p: Profile) => void;
}) {
  const [displayName, setDisplayName] = useState('');
  const [displayNameEdited, setDisplayNameEdited] = useState(false);
  const [phone, setPhone] = useState('');
  const [marketing, setMarketing] = useState(false);
  const [state, setState] = useState<SaveState>('idle');

  useEffect(() => {
    if (!profile) return;
    // AC7 forbids seeding this editable field from the REGISTRY `fullName` — that
    // is rendered separately and read-only as "Name on your account". It says
    // nothing about the pre-existing profile-column fallback, which code review
    // 2026-07-31 found had been dropped in the same edit: a user with first/last
    // names but no displayName saw an empty field where their name used to be.
    setDisplayName(profile.displayName ?? profileNameFallback(profile));
    setDisplayNameEdited(false);
    setPhone(profile.phone ?? '');
    setMarketing(profile.notificationPromoOptedIn);
  }, [profile]);

  const onSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      // Never PATCH before the profile has loaded — the form fields are empty
      // until then, so a submit would wipe the user's real name/phone and opt
      // them out of marketing. The submit button is also disabled in this window.
      if (!profile) return;
      setState('saving');
      try {
        const updated = await updateProfile({
          ...(displayNameEdited ? { displayName: displayName.trim() || null } : {}),
          phone: phone.trim() || null,
          notificationPromoOptedIn: marketing,
        });
        onSaved(updated);
        setState('saved');
      } catch {
        setState('error');
      }
    },
    [profile, displayName, displayNameEdited, phone, marketing, onSaved],
  );

  return (
    <>
      <h2 className={styles.sectionTitle}>Profile</h2>
      <p className={styles.sectionIntro}>
        Keep your name and phone number up to date. Your email, role and account type are managed
        for you and shown here for reference.
      </p>
      <form className={styles.panel} onSubmit={onSubmit}>
        {state === 'saved' ? <Banner type="success">Profile saved.</Banner> : null}
        {state === 'error' ? (
          <Banner type="error">We couldn't save your profile. Please try again.</Banner>
        ) : null}
        <div className={styles.fields}>
          {session?.storeName ? (
            <FormField label="Store name" value={session.storeName} readOnly />
          ) : null}
          <FormField label="Name on your account" value={profile?.fullName ?? ''} readOnly />
          <FormField
            label="Display name"
            value={displayName}
            onChange={(e) => {
              setDisplayName(e.target.value);
              setDisplayNameEdited(true);
            }}
            autoComplete="name"
          />
          {/* Email is Auth0-owned. Prefer the session ID-token claim (identity.email,
              the same source as Role) — the /me/profile access token carries no
              email claim in Phase 1, so profile.email is only a fallback. */}
          <FormField
            label="Email address"
            value={session?.email ?? profile?.email ?? ''}
            readOnly
          />
          <FormField label="Role" value={session?.role ? roleLabel(session.role) : ''} readOnly />
          <FormField label="Account type" value={session?.accountType ?? ''} readOnly />
          <FormField
            label="Phone number"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            inputMode="tel"
            autoComplete="tel"
          />
          <div className={styles.toggleRow}>
            <span className={styles.toggleText}>
              <span className={styles.toggleTitle}>Marketing communications</span>
              <span className={styles.toggleHint}>Promotional emails from BWG</span>
            </span>
            <Toggle
              checked={marketing}
              onChange={setMarketing}
              aria-label="Marketing communications"
            />
          </div>
        </div>
        <div className={styles.actions}>
          <Button type="submit" disabled={!profile || state === 'saving'}>
            {state === 'saving' ? 'Saving…' : 'Save profile'}
          </Button>
        </div>
      </form>
    </>
  );
}

function PasswordPanel({
  onRequestPasswordChange,
}: {
  onRequestPasswordChange: (() => Promise<void>) | null;
}) {
  return (
    <>
      <h2 className={styles.sectionTitle}>Password</h2>
      <p className={styles.sectionIntro}>
        Password changes are handled securely using Auth0 Universal Login. You will be redirected to
        verify your current password and set a new one. Other active sessions are signed out when
        your password is updated.
      </p>
      <div className={styles.panel}>
        <ChangePassword onRequestChange={onRequestPasswordChange} />
      </div>
    </>
  );
}

function NotificationsPanel({
  profile,
  onSaved,
}: {
  profile: Profile | null;
  onSaved: (p: Profile) => void;
}) {
  const [orderEvents, setOrderEvents] = useState(false);
  const [cutoff, setCutoff] = useState(false);
  const [promo, setPromo] = useState(false);
  const [state, setState] = useState<SaveState>('idle');

  useEffect(() => {
    if (!profile) return;
    setOrderEvents(profile.notificationOrderEventsOptedIn);
    setCutoff(profile.notificationCutoffRemindersOptedIn ?? false);
    setPromo(profile.notificationPromoOptedIn);
  }, [profile]);

  const onSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!profile) return; // don't save until the profile has loaded (mirrors Profile tab)
      setState('saving');
      try {
        // OQ-6.8: `notificationCutoffRemindersOptedIn` is NOT sent — the Story 6.7
        // `UpdateProfileDto` has no such field and the API's `whitelist: true`
        // ValidationPipe silently strips it, which would flash a false "saved" and
        // then snap the toggle back. The cut-off toggle stays disabled until the
        // backing column lands (Story 6.7/6.12 follow-up).
        const updated = await updateProfile({
          notificationOrderEventsOptedIn: orderEvents,
          notificationPromoOptedIn: promo,
        });
        onSaved(updated);
        setState('saved');
      } catch {
        setState('error');
      }
    },
    [profile, orderEvents, promo, onSaved],
  );

  return (
    <>
      <h2 className={styles.sectionTitle}>Notifications</h2>
      <form className={styles.panel} onSubmit={onSubmit}>
        {state === 'saved' ? <Banner type="success">Preferences saved.</Banner> : null}
        {state === 'error' ? (
          <Banner type="error">We couldn't save your preferences. Please try again.</Banner>
        ) : null}
        <div className={styles.toggleStack}>
          <div className={styles.toggleCard}>
            <span className={styles.toggleTitle}>Order confirmation emails</span>
            <Toggle
              checked={orderEvents}
              onChange={setOrderEvents}
              aria-label="Order confirmation emails"
            />
          </div>
          <div className={styles.toggleCard}>
            <span className={styles.toggleText}>
              <span className={styles.toggleTitle}>Cut-off reminders</span>
              <span className={styles.toggleHint}>Coming soon — not yet available</span>
            </span>
            <Toggle
              checked={cutoff}
              onChange={setCutoff}
              disabled
              aria-label="Cut-off reminders (coming soon)"
            />
          </div>
          <div className={styles.toggleCard}>
            <span className={styles.toggleTitle}>Promotional alerts</span>
            <Toggle checked={promo} onChange={setPromo} aria-label="Promotional alerts" />
          </div>
        </div>
        <div className={styles.actions}>
          <Button type="submit" disabled={!profile || state === 'saving'}>
            {state === 'saving' ? 'Saving…' : 'Save preferences'}
          </Button>
        </div>
      </form>
    </>
  );
}

/**
 * Save a blob to disk under `filename` via a transient object-URL anchor — the
 * standard browser download for an in-memory artifact (the GDPR export body).
 * The object URL is revoked immediately after the synchronous click.
 */
function triggerBrowserDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

function PrivacyDataPanel() {
  const [exportState, setExportState] = useState<SaveState>('idle');
  const [erasureState, setErasureState] = useState<SaveState>('idle');
  // Erasure is a destructive GDPR action, so it's gated behind the design-system
  // Modal (Story 6.9) rather than window.confirm: the browser can silently
  // suppress a native confirm, and only the Modal provides the focus-trap /
  // Escape / focus-restore a11y behaviour required by NFR24–26.
  const [confirmingErasure, setConfirmingErasure] = useState(false);

  const onExport = useCallback(async () => {
    setExportState('saving');
    try {
      const { blob, filename } = await requestDataExport();
      triggerBrowserDownload(blob, filename);
      // The saved file IS the confirmation — no "we'll email you" banner. Back to
      // idle so the button re-enables for a repeat export.
      setExportState('idle');
    } catch {
      setExportState('error');
    }
  }, []);

  const confirmErasure = useCallback(async () => {
    setConfirmingErasure(false);
    setErasureState('saving');
    try {
      await requestErasure();
      setErasureState('saved');
    } catch {
      setErasureState('error');
    }
  }, []);

  return (
    <>
      <h2 className={styles.sectionTitle}>Privacy &amp; Data</h2>
      <p className={styles.sectionIntro}>Manage your data rights and account privacy settings.</p>
      <div className={styles.panel}>
        <div className={styles.gdprRow}>
          <span className={styles.gdprTitle}>Export my data</span>
          <span className={styles.gdprHint}>
            Download a file with your profile, order history references and audit events.
          </span>
          {exportState === 'error' ? (
            <Banner type="error">We couldn't prepare your export. Please try again.</Banner>
          ) : null}
          <div>
            <Button
              variant="secondary"
              leadingIcon={<Icon name="download" size={16} />}
              onClick={onExport}
              disabled={exportState === 'saving'}
            >
              {exportState === 'saving' ? 'Preparing…' : 'Download data'}
            </Button>
          </div>
        </div>

        <div className={styles.gdprRow}>
          <span className={styles.gdprTitle}>Request account erasure</span>
          <span className={styles.gdprHint}>
            Submit an erasure request under GDPR. Your personal data is pseudonymised or
            crypto-shredded; immutable order and audit records retain non-identifying references
            only, as required for financial and legal record-keeping.
          </span>
          {erasureState === 'saved' ? (
            <Banner type="success">Your erasure request has been submitted.</Banner>
          ) : erasureState === 'error' ? (
            <Banner type="error">We couldn't submit your request. Please try again.</Banner>
          ) : null}
          <div>
            <Button
              variant="destructive"
              leadingIcon={<Icon name="trash" size={16} />}
              onClick={() => setConfirmingErasure(true)}
              disabled={erasureState === 'saving'}
            >
              Request erasure
            </Button>
          </div>
        </div>
      </div>

      <Modal
        open={confirmingErasure}
        onClose={() => setConfirmingErasure(false)}
        title="Request account erasure?"
        description="This submits a GDPR erasure request for your personal data. Your data is pseudonymised or crypto-shredded; immutable order and audit records retain non-identifying references only."
        footer={
          <>
            <Button variant="secondary" onClick={() => setConfirmingErasure(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={confirmErasure}>
              Request erasure
            </Button>
          </>
        }
      >
        This action cannot be undone. Are you sure you want to submit an erasure request?
      </Modal>
    </>
  );
}
